import { Check, Eye, LockKeyhole, Pause, Play, ShieldCheck, XCircle } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  blindPhonemeOrder,
  blindRejectReasons,
  canJudgeCandidate,
  canRevealSources,
  emptyBlindQaState,
  normalizeBlindQaState,
  type BlindQaState,
  type BlindRejectReason,
} from './stage1BlindQaState'

interface CandidateRecord {
  candidateId: string
  phonemeId: string
  ipa: string
  candidateLabel: 'A' | 'B' | 'C'
  sourceWord: string
  durationMs: number
  filePath: string
}

interface CandidateManifest {
  records: CandidateRecord[]
}

const storageKey = 'phonics-garden.stage1-blind-qa.v1'

const readQa = (): BlindQaState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) ?? '') as BlindQaState
    return parsed?.version === 1 && parsed.phonemes ? parsed : emptyBlindQaState()
  } catch { return emptyBlindQaState() }
}

export function Stage1BlindAudioQa({ view }: { view: 'blind' | 'comparison' }) {
  const [manifest, setManifest] = useState<CandidateManifest | null>(null)
  const [loadError, setLoadError] = useState('')
  const [playbackError, setPlaybackError] = useState('')
  const [saveError, setSaveError] = useState(false)
  const [qa, setQa] = useState<BlindQaState>(readQa)
  const [rejectSelections, setRejectSelections] = useState<Record<string, BlindRejectReason>>({})
  const activeAudio = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    let current = true
    void fetch('/audio/stage1-qa-manifest.json')
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json() as Promise<CandidateManifest>
      })
      .then((data) => {
        if (!current) return
        setManifest(data)
        setQa((previous) => {
          const next = normalizeBlindQaState(previous, data.records)
          try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { setSaveError(true) }
          return next
        })
      })
      .catch((error: unknown) => { if (current) setLoadError(error instanceof Error ? error.message : String(error)) })
    return () => { current = false; activeAudio.current?.pause() }
  }, [])

  const recordsById = useMemo(() => new Map(manifest?.records.map((record) => [record.candidateId, record]) ?? []), [manifest])

  const saveQa = (update: (current: BlindQaState) => BlindQaState) => {
    setQa((current) => {
      const next = update(current)
      try { localStorage.setItem(storageKey, JSON.stringify(next)); setSaveError(false) } catch { setSaveError(true) }
      return next
    })
  }

  const stop = () => {
    if (!activeAudio.current) return
    activeAudio.current.pause()
    activeAudio.current.currentTime = 0
    activeAudio.current = null
  }

  const play = (phonemeId: string, candidateId: string) => {
    const record = recordsById.get(candidateId)
    if (!record) return
    stop()
    const audio = new Audio(record.filePath)
    activeAudio.current = audio
    setPlaybackError('')
    audio.addEventListener('ended', () => {
      if (activeAudio.current !== audio) return
      activeAudio.current = null
      saveQa((current) => {
      const review = current.phonemes[phonemeId]
      if (!review || review.playedCandidateIds.includes(candidateId)) return current
      return {
        ...current,
        phonemes: { ...current.phonemes, [phonemeId]: { ...review, playedCandidateIds: [...review.playedCandidateIds, candidateId] } },
      }
      })
    }, { once: true })
    const fail = () => { if (activeAudio.current === audio) { activeAudio.current = null; setPlaybackError('播放未完成，未计入试听。请重试。') } }
    audio.addEventListener('error', fail, { once: true })
    void audio.play().catch(fail)
  }

  const setResult = (phonemeId: string, candidateId: string, result: 'acceptable' | 'rejected') => {
    saveQa((current) => {
      const review = current.phonemes[phonemeId]
      if (!canJudgeCandidate(review, candidateId)) return current
      const rejectReason = result === 'rejected' ? rejectSelections[candidateId] ?? 'other' : undefined
      return {
        ...current,
        phonemes: {
          ...current.phonemes,
          [phonemeId]: {
            ...review,
            candidates: { ...review.candidates, [candidateId]: { result, rejectReason } },
          },
        },
      }
    })
  }

  const reveal = (phonemeId: string) => {
    saveQa((current) => {
      const review = current.phonemes[phonemeId]
      if (!canRevealSources(review)) return current
      return { ...current, phonemes: { ...current.phonemes, [phonemeId]: { ...review, revealed: true } } }
    })
  }

  if (loadError) return <div className="rounded-3xl bg-rose-50 p-6 font-bold text-rose-800">Blind QA manifest 加载失败：{loadError}</div>
  if (!manifest) return <div className="rounded-3xl bg-white p-6 font-bold text-garden-700 shadow-soft">正在载入 Blind QA…</div>

  if (view === 'comparison') {
    return <div className="space-y-5">
      <div className="rounded-3xl border-2 border-garden-200 bg-white p-5 shadow-soft"><div className="flex items-center gap-3"><ShieldCheck className="h-7 w-7 text-garden-700" /><h2 className="text-2xl font-black">Stage 1 Blind QA Comparison</h2></div><p className="mt-2 font-bold text-garden-700">仅显示已经完成盲听并 Reveal Source 的 phoneme；结果不会触发 promotion 或 verified。</p></div>
      {blindPhonemeOrder.map((phonemeId) => {
        const review = qa.phonemes[phonemeId]
        const first = manifest.records.find((record) => record.phonemeId === phonemeId)
        if (!first) return null
        if (!review?.revealed) return <section key={phonemeId} className="rounded-3xl bg-white p-5 shadow-soft"><div className="flex items-center justify-between gap-3"><h3 className="text-3xl font-black">{first.ipa}</h3><span className="inline-flex items-center gap-2 rounded-full bg-garden-100 px-3 py-2 text-sm font-black text-garden-700"><LockKeyhole className="h-4 w-4" />完成 Blind QA 后显示</span></div></section>
        const records = manifest.records.filter((record) => record.phonemeId === phonemeId).sort((left, right) => left.candidateLabel.localeCompare(right.candidateLabel))
        return <section key={phonemeId} className="overflow-hidden rounded-3xl bg-white shadow-soft"><h3 className="px-5 pt-5 text-3xl font-black">{first.ipa}</h3><div className="overflow-x-auto p-5"><table className="w-full min-w-[720px] border-separate border-spacing-y-2 text-left"><thead><tr className="text-sm text-garden-600"><th className="px-3 py-2">phoneme</th><th className="px-3 py-2">candidate</th><th className="px-3 py-2">source word</th><th className="px-3 py-2">duration</th><th className="px-3 py-2">QA result</th><th className="px-3 py-2">reject reason</th></tr></thead><tbody>{records.map((record) => {
          const result = review.candidates[record.candidateId]
          return <tr key={record.candidateId} className="bg-garden-50 font-bold"><td className="rounded-l-2xl px-3 py-3 text-xl font-black">{record.ipa}</td><td className="px-3 py-3">{record.candidateLabel}</td><td className="px-3 py-3">{record.sourceWord}</td><td className="px-3 py-3">{record.durationMs} ms</td><td className={`px-3 py-3 ${result?.result === 'acceptable' ? 'text-emerald-700' : 'text-rose-700'}`}>{result?.result ?? 'pending'}</td><td className="rounded-r-2xl px-3 py-3">{result?.rejectReason ?? '—'}</td></tr>
        })}</tbody></table></div></section>
      })}
    </div>
  }

  return <div className="space-y-6">
    {playbackError && <p role="alert" className="rounded-2xl bg-amber-50 p-4 font-bold text-amber-900">{playbackError}</p>}
    {saveError && <p role="alert" className="rounded-2xl bg-amber-50 p-4 font-bold text-amber-900">浏览器无法保存本次判断，请勿关闭页面。</p>}
    <div className="rounded-3xl border-2 border-garden-200 bg-white p-5 shadow-soft"><div className="flex flex-wrap items-center gap-3"><ShieldCheck className="h-7 w-7 text-garden-700" /><h2 className="text-2xl font-black">Stage 1 Blind QA</h2><span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800">No auto promote</span></div><p className="mt-2 font-bold text-garden-700">先只凭声音判断。每个 phoneme 的三段候选顺序已随机并固定保存在本机；三段都试听、完成判断后才能揭晓来源。</p></div>
    {blindPhonemeOrder.map((phonemeId) => {
      const review = qa.phonemes[phonemeId]
      const first = manifest.records.find((record) => record.phonemeId === phonemeId)
      if (!review || !first) return null
      const playedCount = review.playedCandidateIds.length
      const judgedCount = review.order.filter((id) => review.candidates[id]?.result).length
      const readyToReveal = canRevealSources(review)
      return <section key={phonemeId} className="rounded-[2rem] border-2 border-garden-100 bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3"><h3 className="text-4xl font-black">{first.ipa}</h3><span className={`rounded-full px-3 py-2 text-sm font-black ${review.revealed ? 'bg-emerald-100 text-emerald-800' : 'bg-garden-100 text-garden-700'}`}>{review.revealed ? 'Source revealed' : `试听 ${playedCount}/3 · 判断 ${judgedCount}/3`}</span></div>
        <div className="mt-5 grid gap-4 lg:grid-cols-3">{review.order.map((candidateId, index) => {
          const record = recordsById.get(candidateId)
          if (!record) return null
          const result = review.candidates[candidateId]
          return <article key={candidateId} className={`rounded-3xl border-2 p-4 ${result?.result === 'acceptable' ? 'border-emerald-200 bg-emerald-50/50' : result?.result === 'rejected' ? 'border-rose-200 bg-rose-50/50' : 'border-garden-100 bg-garden-50/50'}`}>
            <h4 className="text-center text-xl font-black">Play {index + 1}</h4>
            <button onClick={() => play(phonemeId, candidateId)} className="mt-3 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-garden-800 px-4 font-black text-white"><Play className="h-5 w-5" />Play {index + 1}</button>
            <div className="mt-3 grid grid-cols-2 gap-2"><button onClick={() => setResult(phonemeId, candidateId, 'acceptable')} disabled={!canJudgeCandidate(review, candidateId)} className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-black disabled:cursor-not-allowed ${result?.result === 'acceptable' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800'} disabled:opacity-70`}><Check className="h-4 w-4" />Acceptable</button><button onClick={() => setResult(phonemeId, candidateId, 'rejected')} disabled={!canJudgeCandidate(review, candidateId)} className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-black disabled:cursor-not-allowed ${result?.result === 'rejected' ? 'bg-rose-600 text-white' : 'bg-rose-100 text-rose-800'} disabled:opacity-70`}><XCircle className="h-4 w-4" />Reject</button></div>
            {!review.revealed && <select aria-label={`Play ${index + 1} reject reason`} value={rejectSelections[candidateId] ?? result?.rejectReason ?? 'other'} onChange={(event) => {
              const reason = event.target.value as BlindRejectReason
              setRejectSelections((current) => ({ ...current, [candidateId]: reason }))
              if (result?.result === 'rejected') saveQa((current) => {
                const item = current.phonemes[phonemeId]
                if (!canJudgeCandidate(item, candidateId)) return current
                return { ...current, phonemes: { ...current.phonemes, [phonemeId]: { ...item, candidates: { ...item.candidates, [candidateId]: { result: 'rejected', rejectReason: reason } } } } }
              })
            }} className="mt-2 min-h-12 w-full rounded-2xl bg-white px-3 text-sm font-bold">{blindRejectReasons.map((reason) => <option key={reason}>{reason}</option>)}</select>}
            {review.revealed && <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-2xl bg-white p-3 text-sm"><dt className="font-black">candidate</dt><dd className="font-bold">{record.candidateLabel}</dd><dt className="font-black">source</dt><dd className="font-bold">{record.sourceWord}</dd><dt className="font-black">duration</dt><dd className="font-bold">{record.durationMs} ms</dd><dt className="font-black">result</dt><dd className="font-bold">{result?.result}{result?.rejectReason ? ` · ${result.rejectReason}` : ''}</dd></dl>}
          </article>
        })}</div>
        {!review.revealed && <div className="mt-5 flex flex-wrap items-center justify-between gap-3"><p className="font-bold text-garden-700">三段都试听并完成 Acceptable / Reject 后，才能 Reveal Source。</p><button onClick={() => reveal(phonemeId)} disabled={!readyToReveal} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-violet-700 px-5 font-black text-white disabled:cursor-not-allowed disabled:opacity-35"><Eye className="h-5 w-5" />Reveal Source</button></div>}
      </section>
    })}
    <button onClick={stop} className="fixed bottom-24 right-5 z-30 grid h-14 w-14 place-items-center rounded-full bg-garden-900 text-white shadow-soft" aria-label="停止播放"><Pause /></button>
  </div>
}
